'use client'

import { shouldForwardProp } from '@/features/shared/utils/appStart/shouldForwardPropForStyledComponentV6'
import { LazyMotion } from 'framer-motion'
import { type ReactNode } from 'react'
import { Provider as JotaiProvider } from 'jotai'
import { StyleSheetManager, ThemeProvider } from 'styled-components'
import { useCreateJotaiStore } from '@/features/shared/state'
import { theme } from '@/features/shared/constants/shiptTheme'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { QueryClientProvider } from '@tanstack/react-query'
import { GlobalStyle } from '@/features/shared/styles/globalStyles'
import { ConsentProvider } from '@/lib/transcend/useConsentManager'
import { isOnServer } from '@shared/constants/util'

const loadFeatures = async () => (await import('@/lib/framerMotion')).domAnimation

/**
 * Detects the prerender crawler. Used to disable
 * styled-components' CSSOM injection so post-hydration styles land in real
 * `<style>` tags that the bot's DOM snapshot can capture.
 * https://docs.prerender.io/docs/how-to-fix-css-caching-issues-in-react-sites
 * Always false during SSR — `window` is undefined on the server, and
 * setting `disableCSSOMInjection` on the server would break the parent
 * ServerStyleSheet's collection.
 */
const isPrerenderClient =
  !isOnServer() && navigator.userAgent.toLowerCase().indexOf('prerender') !== -1

export const RootProviders = ({ children }: { children: ReactNode }) => {
  const queryClient = getQueryClient()
  const jotaiStore = useCreateJotaiStore()

  return (
    <JotaiProvider store={jotaiStore}>
      <QueryClientProvider client={queryClient}>
        <StyleSheetManager
          shouldForwardProp={shouldForwardProp}
          enableVendorPrefixes
          disableCSSOMInjection={isPrerenderClient}
        >
          <ThemeProvider theme={theme}>
            <LazyMotion features={loadFeatures} strict>
              <GlobalStyle />
              <ConsentProvider>{children}</ConsentProvider>
            </LazyMotion>
          </ThemeProvider>
        </StyleSheetManager>
      </QueryClientProvider>
    </JotaiProvider>
  )
}
